import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo } from "react";

import { ErrorComponent } from "@/components/common/errors";
import { HorizontalLevels } from "@/components/dataPages";
import { TranslatedText } from "@/components/common/text";
import { InfoButton } from "@/components/common/buttons";
import { ComparisonValues } from "@/utils/constants";
import { useQuery } from "@/utils/query";
import { getComparisonAndConfidence } from "@/state/marketPotentialSlice";

export const ComparisonAndConfidence = () => {
    const { data: value, loading, error } = useSelector((state) => state.marketPotential);
    const { trailerType, pickupZip, pickupDate } = useQuery();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getComparisonAndConfidence({ trailerType, pickupZip, pickupDate }));
    }, [trailerType, pickupZip, pickupDate]);

    const comparisonIndex = useMemo(() => {
        if (!value) return null;
        return ComparisonValues.findIndex((cv) => cv === value.comparison);
    }, [value]);

    const certaintyIndex = useMemo(() => {
        if (!value) return null;
        switch (value.confidence) {
            case "high":
                return 2;
            case "medium":
                return 1;
            case "low":
                return 0;
            default:
                return null;
        }
    }, [value]);

    const certaintyTexts = {
        0: "There might be a chance to find load here",
        1: "There is fair chance to find load here",
        2: "There is high chance to find load here",
    };

    const comparisonTexts = {
        0: "Majority of other markets are more profitable than this one",
        1: "Less profitable than most markets",
        2: "Average paying market",
        3: "More profitable than most markets",
        4: "One of the best markets available currently",
    };

    return (
        <div className="flex w-full flex-1">
            {loading ? (
                <div className="min-h-[106px] h-[106px] w-full" />
            ) : error ? (
                <ErrorComponent className="min-h-[106px] h-[106px]" />
            ) : (
                <div className="h-[106px] w-full flex flex-col justify-between">
                    <div className="flex flex-col min-h-[41px] max-h-[41px] w-full mb-6">
                        <div className="flex flex-col gap-2 mb-2">
                            <div className="text-cm-p-text-primary text-base/[25px] flex items-center">
                                <TranslatedText>Comparison to other markets</TranslatedText>
                                <div className="ml-[10px]">
                                    <InfoButton text={comparisonTexts[comparisonIndex]} />
                                </div>
                            </div>
                        </div>
                        <HorizontalLevels level={comparisonIndex ?? 0} maxLevel={5} />
                    </div>
                    <div className="flex flex-col min-h-[41px] max-h-[41px] w-full">
                        <div className="flex flex-col gap-2 mb-2">
                            <div className="text-cm-p-text-primary text-base/[25px] flex items-center">
                                <TranslatedText>Load certainty</TranslatedText>
                                <div className="ml-[10px]">
                                    <InfoButton text={certaintyTexts[certaintyIndex]} />
                                </div>
                            </div>
                        </div>
                        <HorizontalLevels level={certaintyIndex ?? 0} maxLevel={3} />
                    </div>
                </div>
            )}
        </div>
    );
};
